class Form {
  constructor(element, options) {
    this._options = { ...Form.options, ...options }
    this._element = element
    this._init()
  }

  static options = {
    requiredText: 'This field is required',
    invalidEmailText: 'The email address you entered is invalid',
    submittingText: 'Submitting...',
    successTitle: 'Thank you for the nomination!',
    successDescription: 'You will receive the confirmation email soon.',
    errorTitle: 'Sorry, something went wrong.',
    errorDescription:
      'Unfortunately, we could not process your nomination at the moment. Please try again later.',
    closeStatusText: 'Close',
  }

  _state = {
    isFormVisible: false,
    scrollTop: 0,
    textarea: null,
    canSubmit: false,
    formValidated: false,
    errors: 0,
    isSubmitting: false,
  }

  _init() {
    this._cacheSelectors()
    this._initUI()
  }

  _cacheSelectors() {
    this._fields = [
      ...this._element.querySelectorAll(
        'input.FormTextbox__Input, input[type="radio"], select',
      ),
    ]
    this._closeForm = document.querySelectorAll('.button--close')
    this._openForm = document.querySelectorAll('.open-form')
    this._body = document.body
    this._html = document.documentElement
    this._content = this._element.querySelector('.form__content')
    this._submitButton = this._element.querySelector('.FormSubmitButton')
  }

  _initUI() {
    const isVisible = this._state.isFormVisible
    this._state.isFormVisible = isVisible
    this._body.setAttribute('data-modal', isVisible)

    this._openForm.forEach((toggle) =>
      toggle.addEventListener('click', () => this._handleOpen()),
    )

    this._closeForm.forEach((close) =>
      close.addEventListener('click', (e) => this._handleClose(e)),
    )

    this._submitButton.addEventListener('click', (e) => {
      e.preventDefault()
      if (this._validateForm()) {
        this._handleSubmit()
      }
    })
  }

  _handleClose(e) {
    e.preventDefault()
    this._element.classList.add('hidden')
    this._element.classList.remove('visible')
    this._body.setAttribute('data-modal', false)
    this._state.isFormVisible = false
    this._body.scrollTop = this._html.scrollTop = this._state.scrollTop
  }

  _handleOpen() {
    this._state.scrollTop = this._body.scrollTop || this._html.scrollTop
    this._body.setAttribute('data-modal', true)
    this._element.classList.remove('hidden')
    this._element.classList.add('visible')
    this._state.isFormVisible = true
  }

  _validateForm() {
    const requiredFields = [...this._element.querySelectorAll('[required]')]
    let isValid = true
    let firstError = null

    requiredFields.forEach((field) => {
      if (!this._validateField(field)) {
        if (!firstError) firstError = field
        isValid = false
      }
    })

    if (firstError) {
      firstError.closest('.Form__Element').scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      firstError.focus()
    }

    this._submitButton.setAttribute('data-submit', isValid)
    return isValid
  }

  _resetValidation() {
    const elements = this._element.querySelectorAll('.Form__Element')
    elements.forEach((el) => {
      el.setAttribute('data-error', 'false')
      const errorMsg = el.querySelector('.Form__Element__ValidationError')
      if (errorMsg) errorMsg.textContent = ''
    })
  }

  _validateField(field) {
    let isEmpty = field.value.trim() === ''
    const isEmailInvalid =
      field.type === 'email' && !Form._validateEmail(field.value)

    if (field.type === 'radio') {
      const radioGroup = this._element.querySelectorAll(
        `input[name="${field.name}"]`,
      )
      isEmpty = ![...radioGroup].some((radio) => radio.checked)
    }

    const isValid = !isEmpty && !isEmailInvalid

    const formElement = field.closest('.Form__Element')
    this._updateFormElementStatus(formElement, isValid, isEmpty)

    return isValid
  }

  _updateFormElementStatus(formElement, isValid, isEmpty) {
    formElement.setAttribute('data-error', !isValid)
    formElement.setAttribute('data-focus', false)

    const errorMessage = formElement.querySelector(
      '.Form__Element__ValidationError',
    )
    if (!isValid && errorMessage) {
      errorMessage.textContent = isEmpty
        ? this._options.requiredText
        : this._options.invalidEmailText
    }
  }

  static _validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  _handleSubmit() {
    this._state.isSubmitting = true
    this._submitButton.textContent = this._options.submittingText
    this._submitButton.disabled = true

    const data = this._getFormData()

    fetch(this._element.getAttribute('action'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (!response.ok) throw new Error(this._options.errorText)
        this._openStatus(
          this._options.successTitle,
          this._options.successDescription,
        )
      })
      .catch((error) => {
        console.error('Error:', error)
        this._openStatus(
          this._options.errorTitle,
          this._options.errorDescription,
        )
      })
  }

  _getFormData() {
    return Object.fromEntries(new FormData(this._element).entries())
  }

  _openStatus(title, description) {
    this._state.isSubmitting = false
    this._content.insertAdjacentHTML(
      'beforeend',
      `<div class="form__status">
        <h1>${title}</h1>
        <p>${description}</p>
        <button type="button" class="button button--primary form__status__close">${this._options.closeStatusText}</button>
      </div>`,
    )
    this._content
      .querySelector('.form__status__close')
      .addEventListener('click', (e) => this._closeStatus(e))
  }

  _closeStatus(e) {
    this._handleClose(e)
    this._element.reset()
    this._content.querySelector('.form__status').remove()
  }
}

export default Form
